import React from 'react';
import PropTypes from 'prop-types';

import SourceDataButton, {
  pickFields,
  FieldSource,
  FooterSourceToggle,
  useFooterSource,
} from './ChartSourceModal';
import {
  WIDTH,
  HEIGHT,
  CX,
  CY,
  OUTER_R,
  INNER_R,
  parseLocaleNumber,
  polarToXY,
  segmentIndexToAngle,
  arcPath,
  needlePath,
  needleValueToAngle,
  formatWeight,
  formatPercentage,
  isBlankFooterValue,
} from './gaugeGeometry';

/**
 * Koud geslacht gewicht gauge.
 *
 * Seven fixed bands:
 *   <65 | 65-80 | 80-85 | 85-105 | 105-115 | 115-125 | >125
 *
 * Each segment shows the share of the delivery whose cold carcass weight falls
 * in that band, folded up from the backend's own per-animal weight categories
 * ("Verdeling gewichtscategorie"). Bands with no animals show 0%.
 *
 * The needles carry the averages: red from klassement_totalen 236, black from
 * the BPG benchmark (drgmbmk DEGEMGW, week -1, DELTMCE = ALL).
 */

// The backend's weight-category key, e.g. { 'Koud Geslacht Gewicht': '90-95' }.
const CATEGORY_KEY = 'Koud Geslacht Gewicht';
const SHARE_KEY = '% Aantal';

// Upper bound of each of the first six bands; the seventh is open-ended.
export const GEWICHT_BOUNDARIES = [65, 80, 85, 105, 115, 125];
export const GEWICHT_SEGMENT_COUNT = GEWICHT_BOUNDARIES.length + 1;
export const GEWICHT_LABELS = GEWICHT_BOUNDARIES.map((b) => `${b} kg`);

// Provisional palette, reusing the values already on this page: out-of-range
// extremes red, the optimum in the middle green. Confirm with the client.
export const GEWICHT_COLORS = [
  '#8B0000', '#e07070', '#f0c840', '#2d6a2d', '#f0c840', '#e07070', '#8B0000',
];

/**
 * Which band an average cold weight falls into.
 * Bands are lower-inclusive / upper-exclusive, matching the vleesdikte gauge.
 */
export function getGewichtSegmentIndex(avgWeight) {
  for (let i = 0; i < GEWICHT_BOUNDARIES.length; i++) {
    if (avgWeight < GEWICHT_BOUNDARIES[i]) {
      return i;
    }
  }
  return GEWICHT_BOUNDARIES.length;
}

/**
 * Read a weight category label into a band index.
 * Handles '90-95' as well as open-ended '<65' / '>125'. Returns null when the
 * label carries no number at all.
 */
function categoryToSegmentIndex(label) {
  if (label == null) {
    return null;
  }

  const text = String(label);
  const match = text.match(/(\d+(?:[.,]\d+)?)/);
  if (!match) {
    return null;
  }

  const bound = parseLocaleNumber(match[1], null);
  if (bound == null) {
    return null;
  }

  // '<65' belongs in the open low band, '>125' in the open high band.
  if (text.includes('<')) {
    return 0;
  }
  if (text.includes('>')) {
    return GEWICHT_SEGMENT_COUNT - 1;
  }

  return getGewichtSegmentIndex(bound);
}

/**
 * Fold the backend's weight categories into the seven bands.
 *
 * verdeling[i]['Koud Geslacht Gewicht'] = category label, e.g. '90-95'
 * verdeling[i]['% Aantal']              = share of the delivery in that category
 *
 * A category is placed by its lower bound. Every backend boundary is a multiple
 * of 5 and so are the band edges, so no category straddles a band.
 */
export function buildGewichtSegments(verdeling) {
  const shares = Array(GEWICHT_SEGMENT_COUNT).fill(0);

  if (Array.isArray(verdeling)) {
    verdeling.forEach((item) => {
      const index = categoryToSegmentIndex(item[CATEGORY_KEY]);
      if (index == null) {
        return;
      }
      shares[index] += parseLocaleNumber(item[SHARE_KEY], 0);
    });
  }

  return Array.from({ length: GEWICHT_SEGMENT_COUNT }, (_, i) => ({
    min: i === 0 ? null : GEWICHT_BOUNDARIES[i - 1],
    max: i < GEWICHT_BOUNDARIES.length ? GEWICHT_BOUNDARIES[i] : null,
    color: GEWICHT_COLORS[i],
    label: i < GEWICHT_LABELS.length ? GEWICHT_LABELS[i] : null,
    percentage: shares[i],
  }));
}

// Distinct from the other gauges' filter so the page has no duplicate DOM ids.
const SHADOW_FILTER_ID = 'gewicht-needle-shadow';
const LABEL_OFFSET = 22;
const NEEDLE_RED = '#dc2626';
const NEEDLE_BLACK = '#000';

function GewichtGauge({ segments, zValue, bpgValue, showShares = true }) {
  const needleAngle = (v) =>
    needleValueToAngle(v, GEWICHT_BOUNDARIES, GEWICHT_SEGMENT_COUNT);

  return (
    <svg
      viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
      role='img'
      aria-label='Koud geslacht gewicht gauge'
    >
      <defs>
        <filter id={SHADOW_FILTER_ID} x='-20%' y='-20%' width='140%' height='140%'>
          <feDropShadow dx='1' dy='1' stdDeviation='2' floodOpacity='0.3' />
        </filter>
      </defs>

      {segments.map((seg, i) => {
        const a1 = segmentIndexToAngle(i, GEWICHT_SEGMENT_COUNT);
        const a2 = segmentIndexToAngle(i + 1, GEWICHT_SEGMENT_COUNT);
        const mid = polarToXY((a1 + a2) / 2, (OUTER_R + INNER_R) / 2);
        const labelPos = polarToXY(a2, OUTER_R + LABEL_OFFSET);

        return (
          <g key={i}>
            <path
              data-segment={i}
              d={arcPath(a1, a2, OUTER_R, INNER_R)}
              fill={seg.color}
              stroke='white'
              strokeWidth='0.8'
            />

            {/* Boundary label at the segment's upper edge; the open-ended last band has none */}
            {seg.label != null && (
              <text
                x={labelPos.x}
                y={labelPos.y}
                textAnchor='middle'
                fontSize='11'
                fontWeight='bold'
                fill='#000'
              >
                {seg.label}
              </text>
            )}

            {/* Share of the delivery in this band; 0% is shown, not hidden */}
            {showShares && (
              <text
                data-share
                x={mid.x}
                y={mid.y + 5}
                textAnchor='middle'
                fontSize='12'
                fontWeight='600'
                fill='#fff'
                pointerEvents='none'
                >
                {formatPercentage(seg.percentage)}
              </text>
            )}

          </g>
        );
      })}

      {/* Red needle: this delivery's average (field 236) */}
      {zValue != null && (
        <path
          d={needlePath(needleAngle(zValue), INNER_R - 10, 5)}
          fill={NEEDLE_RED}
          filter={`url(#${SHADOW_FILTER_ID})`}
        />
      )}

      {/* Black needle: BPG benchmark average (drgmbmk DEGEMGW, week -1, ALL) */}
      {bpgValue != null && (
        <path
          d={needlePath(needleAngle(bpgValue), ((INNER_R - 10) * 3) / 4, 5)}
          fill={NEEDLE_BLACK}
          filter={`url(#${SHADOW_FILTER_ID})`}
        />
      )}

      <circle cx={CX} cy={CY} r={10} fill='#888' />
      <circle cx={CX} cy={CY} r={5} fill='#fff' />
    </svg>
  );
}

/**
 * The value as the backend sends it, so the footer reads exactly as specified
 * (field 506 arrives as '0,0', field 216 as '0'). Falls back to a formatted
 * number when the field is missing.
 */
function footerValue(raw) {
  if (raw != null && String(raw).trim() !== '') {
    return String(raw).trim();
  }
  return formatWeight(parseLocaleNumber(raw, 0));
}

function GewichtFooter({ rows }) {
  // In development and staging every row is shown, so the field wiring can be
  // checked against the payload; elsewhere a row with no value is dropped.
  const source = useFooterSource();
  const visibleRows = source.available
    ? rows
    : rows.filter((row) => !isBlankFooterValue(row.value));

  return (
    <div style={{ fontSize: 13, color: '#555', lineHeight: '1.8', textAlign: 'center' }}>
      {visibleRows.map((row) => (
        <div
          key={row.label}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
        >
          {row.color ? (
            <span
              style={{
                display: 'inline-block',
                width: 10,
                height: 10,
                borderRadius: 2,
                background: row.color,
              }}
            />
          ) : (
            <span style={{ display: 'inline-block', width: 10 }} />
          )}
          <span>{`${row.label} (AVG: ${row.value})`}</span>
          <FieldSource source={row.source} shown={source.shown} />
        </div>
      ))}
      <FooterSourceToggle
        available={source.available}
        shown={source.shown}
        onToggle={source.toggle}
      />
    </div>
  );
}

/**
 * The gauge without its footer or shares, for a page that only has two averages
 * to show. A needle whose value is missing is not drawn.
 */
export function GewichtAverageGauge({ redValue, blackValue }) {
  return (
    <GewichtGauge
      segments={buildGewichtSegments(null)}
      zValue={redValue}
      bpgValue={blackValue}
      showShares={false}
    />
  );
}

/**
 * Koud geslacht gewicht panel: gauge plus footer.
 *
 * Reads its own fields off the statistics payload so the caller only has to
 * supply the payload and the benchmark value.
 */
function GewichtChartPanel({ data, bpgValue, title }) {
  const segments = buildGewichtSegments(data ? data['Verdeling gewichtscategorie'] : null);
  const kt = data && data.klassement_totalen;

  const totaalValue = parseLocaleNumber(kt && kt['236']); // red needle
  // Every gauge lists its rows in the same order: Z, M, B, P, Totaal, BPG.
  const rows = [
    { label: 'Z', source: '186', value: footerValue(kt && kt['186']), color: null },
    { label: 'M', source: '216', value: footerValue(kt && kt['216']), color: null },
    { label: 'B', source: '506', value: footerValue(kt && kt['506']), color: null },
    { label: 'P', source: '531', value: footerValue(kt && kt['531']), color: null },
    { label: 'Totaal', source: '236', value: footerValue(kt && kt['236']), color: NEEDLE_RED },
  ];

  if (bpgValue != null) {
    rows.push({ label: 'BPG', source: 'BPG benchmark (drgmbmk, week -1, ALL)', value: formatWeight(bpgValue), color: NEEDLE_BLACK });
  }

  // The payload slice this gauge reads, for the source-data dialog.
  const source = {
    'Verdeling gewichtscategorie': data ? data['Verdeling gewichtscategorie'] : null,
    klassement_totalen: pickFields(kt, ['236', '186', '506', '216', '531']),
    benchmark: { source: 'drgmbmk', field: 'DEGEMGW', deltmce: 'ALL', value: bpgValue },
  };

  return (
    <div className='statistics-chart-panel'>
      <div className='statistics-chart-header'>
        <h3>{title}</h3>
        <SourceDataButton title={title} source={source} />
      </div>
      <GewichtGauge segments={segments} zValue={totaalValue} bpgValue={bpgValue} />
      <GewichtFooter rows={rows} />
    </div>
  );
}

GewichtChartPanel.propTypes = {
  data: PropTypes.object,
  bpgValue: PropTypes.number,
  title: PropTypes.string,
};

GewichtChartPanel.defaultProps = {
  data: null,
  bpgValue: null,
  title: 'Koud geslacht gewicht',
};

export default GewichtChartPanel;
